import { useState } from 'react'
import { useRuns, useDeleteRun } from '../hooks/useRuns'
import { RunsList } from '../components/runs/RunsList'
import { CreateRunDialog } from '../components/runs/CreateRunDialog'
import { Button } from '../components/ui/button'
import { RunCardSkeleton } from '../components/loading/SkeletonLoaders'
import { Alert, AlertDescription } from '../components/ui/alert'
import { Plus, AlertTriangle, Zap, Loader2 } from 'lucide-react'

export default function Dashboard() {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null)
  const { data: runs, isLoading, error } = useRuns()
  const deleteRunMutation = useDeleteRun()

  const handleDeleteRun = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this run?')) {
      setDeletingRunId(id)
      try {
        await deleteRunMutation.mutateAsync(id)
      } finally {
        setDeletingRunId(null)
      }
    }
  }

  return (
    <div className="space-y-10">
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal p-8">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-700 rounded-xl">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-primary-900 mb-2">
                My Runs
              </h1>
              <p className="text-neutral-600">
                Plan your ultra running adventures
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            disabled={isLoading}
            className="bg-primary-700 hover:bg-primary-800 text-white border-0 shadow-minimal hover:shadow-minimal-lg transition-all duration-200 px-6"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            New Run
          </Button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <Alert variant="destructive" className="rounded-xl border-accent-200 bg-accent-50">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load runs: {error.message}. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <RunCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Loaded state */}
      {!isLoading && !error && (
        <RunsList
          runs={runs || []}
          onDeleteRun={handleDeleteRun}
          deletingRunId={deletingRunId}
        />
      )}

      <CreateRunDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  )
}